import { useEffect, useRef } from 'react';
import styles from './LottieLoader.module.scss';

const LAYOUT_CLASS = Object.freeze({
  inline: styles.inline,
  block: styles.block,
  fullscreen: styles.fullscreen,
});

const SIZE_CLASS = Object.freeze({
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
});

export function LottieLoader({
  label = 'Loading',
  layout = 'inline',
  size = 'md',
  showLabel = false,
  fullscreen = false,
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const container = containerRef.current;
    if (!container) {
      return undefined;
    }

    const prefersReducedMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    let cancelled = false;
    let animation = null;
    let onDomLoaded = null;

    (async () => {
      try {
        const module = await import('lottie-web');
        if (cancelled) return;

        const lottie = module?.default ?? module;
        animation = lottie.loadAnimation({
          container,
          renderer: 'svg',
          loop: !prefersReducedMotion,
          autoplay: true,
          path: '/loading.json',
        });

        if (prefersReducedMotion) {
          onDomLoaded = () => {
            try {
              animation?.goToAndStop(0, true);
            } catch (error) {
              void error;
            }
          };
          animation.addEventListener('DOMLoaded', onDomLoaded);
        }
      } catch (error) {
        void error;
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (animation && onDomLoaded) {
          animation.removeEventListener('DOMLoaded', onDomLoaded);
        }
        animation.destroy();
      } catch (error) {
        void error;
      }
    };
  }, []);

  const resolvedLayout = fullscreen ? 'fullscreen' : layout;
  const layoutClass = LAYOUT_CLASS[resolvedLayout] || LAYOUT_CLASS.inline;
  const sizeClass = SIZE_CLASS[size] || SIZE_CLASS.md;

  return (
    <div className={`${styles.root} ${layoutClass}`} role="status" aria-live="polite" aria-label={label}>
      <div ref={containerRef} className={`${styles.animation} ${sizeClass}`} aria-hidden="true" />
      {showLabel ? <p className={styles.label}>{label}</p> : null}
    </div>
  );
}
